import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthPageLayout } from "./auth-page-layout";
import { AuthTermsNotice } from "./auth-terms-notice";
import {
  desktopAvailabilityNotice,
  desktopOsName,
  getDesktopDownloadUrl,
  getDesktopInstallerUrl,
  hasDesktopBuild,
} from "@/lib/desktop-download";
import {
  trackDesktopHandoffViewed,
  trackDesktopOpenClicked,
  trackDesktopOpenOutcome,
  trackDesktopDownloadStarted,
} from "@/lib/posthog";
import { Icons } from "@/components/ui/icons";
import { buttonVariants } from "@/components/ui/button";
import { getDesktopDeepLinkUrl } from "@/lib/desktop-deep-link";
import {
  launchDesktopApp,
  type DesktopLaunchOutcome,
} from "@/lib/desktop-deep-link-launcher";
import { useAuth } from "@/hooks/use-auth";
import { useT } from "@/i18n";
import { authMessages } from "@/i18n/messages/auth";

type DownloadState = "idle" | "started";

export function DesktopHandoffPage() {
  const t = useT(authMessages);
  // Two different links, and the difference matters.
  //
  // `installerUrl` points straight at this channel's installer, so the browser
  // saves the file where the user already is. That is what the button below
  // uses, and what the login page has always used.
  //
  // `downloadUrl` is the marketing page, kept for the platforms with no build
  // at all: there is nothing to hand them, and that page is where the "coming
  // soon" story lives. It used to serve both, which meant a signed-in user
  // asking for a download was sent to a public web page to ask again - and got
  // nothing when that page did not yet know their platform.
  const installerUrl = getDesktopInstallerUrl();
  const downloadUrl = getDesktopDownloadUrl();
  const desktopAppUrl = getDesktopDeepLinkUrl("auth");
  // Named `onMac` historically; it now means "this OS has a build", which is
  // what every use of it below actually asks.
  const onMac = hasDesktopBuild();
  // Null only on a platform with no build, where none of the copy below
  // renders. "Mac" is the fallback purely so the type is not nullable.
  const osName = desktopOsName() ?? "Mac";
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [openFailed, setOpenFailed] = useState(false);
  // A direct installer link gives the page no progress or completion signal,
  // so this only records that the click happened and the browser was handed
  // the file. Without it the button looks unchanged and people click again.
  // The started state points at the browser's own downloads list, which is
  // the only place real progress exists.
  const [downloadState, setDownloadState] = useState<DownloadState>("idle");

  useEffect(() => {
    trackDesktopHandoffViewed(onMac);
  }, [onMac]);

  const handleDownloadClick = () => {
    trackDesktopDownloadStarted("desktop_handoff", osName);
    // The Open link and its failure hint leave with the idle state, so a
    // launch still in flight must not report back into the started card.
    launchCleanupRef.current?.();
    launchCleanupRef.current = null;
    setOpenFailed(false);
    setDownloadState("started");
  };

  // The clicked Download button unmounts with the idle state; move focus to
  // the new heading so keyboard and screen-reader users land on the result.
  const startedHeadingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (downloadState === "started") startedHeadingRef.current?.focus();
  }, [downloadState]);

  const handleLaunchOutcome = (outcome: DesktopLaunchOutcome) => {
    trackDesktopOpenOutcome(outcome);
    setOpenFailed(outcome === "failed");
  };

  const handleSignOut = async () => {
    try {
      await logout();
    } catch {
      // Logout API failed (e.g. CSRF mismatch) — still leave the page so
      // the button never silently dead-ends the only screen browser
      // users can reach.
    }
    navigate("/auth/login", { replace: true });
  };

  // Browsers cache "denker-app:// has no handler" per document. If the
  // user clicked Open before installing, plain anchor navigation will
  // silently no-op on later clicks. `launchDesktopApp` watches for the
  // tab going hidden and force-reloads once if it doesn't, flushing the
  // browser-side scheme cache.
  const launchCleanupRef = useRef<(() => void) | null>(null);
  // Empty dep array — runs ONLY on unmount, never on re-render, so this
  // cannot loop. The launcher's own cleanup is idempotent, so a stray
  // call after the launch has already resolved is a no-op. Without this,
  // an SPA navigation away mid-launch leaves the timer + listeners alive
  // on a stale closure.
  useEffect(
    () => () => {
      launchCleanupRef.current?.();
    },
    [],
  );
  const handleOpenDesktop = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Preserve modifier-clicks / middle-click / right-click → defer to
    // the browser's default for the <a href>.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    trackDesktopOpenClicked();
    launchCleanupRef.current?.();
    launchCleanupRef.current = launchDesktopApp(desktopAppUrl, {
      onOutcome: handleLaunchOutcome,
    });
  };

  const openFailedHint = openFailed ? (
    <div className="auth-hint" data-testid="desktop-handoff-open-failed">
      <Icons.Info className="auth-hint-icon h-4 w-4" aria-hidden="true" />
      <p className="auth-hint-text">{t("openFailedHint")}</p>
    </div>
  ) : null;

  const accountRow =
    isAuthenticated && user ? (
      <p
        className="m-0 flex flex-wrap items-center justify-center gap-x-1.5 text-center text-appkit-caption text-[color:var(--auth-ink-secondary)]"
        data-testid="desktop-handoff-account"
      >
        <span className="min-w-0 break-all opacity-70">{user.email} ·</span>{" "}
        <button
          type="button"
          onClick={handleSignOut}
          className="rounded-sm font-semibold transition-colors hover:text-[color:var(--auth-ink)] focus-visible:text-[color:var(--auth-ink)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--auth-accent)]"
          data-testid="desktop-handoff-signout"
        >
          {t("signOut")}
        </button>
      </p>
    ) : null;

  const belowCard =
    openFailedHint || accountRow ? (
      <div className="flex flex-col items-center gap-4">
        {openFailedHint}
        {accountRow}
      </div>
    ) : null;

  return (
    <AuthPageLayout belowCard={belowCard}>
      <div className="auth-state-stack" data-testid="desktop-handoff">
        {onMac ? (
          downloadState === "started" ? (
            <>
              <span
                className="-mb-0.5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[color:color-mix(in_srgb,var(--color-accent)_14%,transparent)] text-accent"
                aria-hidden="true"
              >
                <Icons.Check className="h-[22px] w-[22px]" strokeWidth={2.4} />
              </span>
              <div
                className="flex flex-col gap-3"
                role="status"
                data-testid="desktop-handoff-download-status"
              >
                <h2
                  ref={startedHeadingRef}
                  tabIndex={-1}
                  className="auth-page-title outline-none"
                >
                  {t("downloadStarted")}
                </h2>
                <p className="auth-page-copy">
                  {osName === "Windows"
                    ? t("openInstallerWindows")
                    : t("openInstallerMac")}
                </p>
              </div>
              <a
                href={installerUrl}
                onClick={handleDownloadClick}
                className="auth-small-link"
                data-testid="desktop-handoff-download-again"
              >
                {t("downloadAgain")}
              </a>
            </>
          ) : (
            <>
              <h2 className="auth-page-title">
                {t("denkerForOs", { os: osName })}
              </h2>
              <div className="flex w-full flex-col items-center gap-3.5">
                <a
                  href={installerUrl}
                  onClick={handleDownloadClick}
                  className={buttonVariants({
                    variant: "secondary",
                    size: "lg",
                    className:
                      "auth-liquid-primary-button w-full font-semibold",
                  })}
                  data-testid="desktop-handoff-download"
                >
                  {t("download")}
                </a>
                <AuthTermsNotice action="download" />
              </div>
              <a
                href={desktopAppUrl}
                onClick={handleOpenDesktop}
                className="auth-small-link"
                data-testid="desktop-handoff-open"
              >
                {t("alreadyInstalled")}
              </a>
            </>
          )
        ) : (
          <>
            <h2 className="auth-page-title">{t("runsOnDesktop")}</h2>
            <p className="auth-page-copy">{desktopAvailabilityNotice()}</p>

            <a
              href={downloadUrl}
              onClick={() =>
                trackDesktopDownloadStarted("desktop_handoff", osName)
              }
              className={buttonVariants({
                variant: "default",
                size: "lg",
                className: "auth-liquid-primary-button w-full font-semibold",
              })}
              data-testid="desktop-handoff-download"
            >
              {t("getDenkerForOs", { os: osName })}
            </a>
          </>
        )}
      </div>
    </AuthPageLayout>
  );
}
